<script setup lang='ts'>
import type { FormInst, FormItemRule, FormRules } from 'naive-ui'
import Quality from './Quality.vue'
import Editor from './Editor.vue'
import { validateHandle } from '~/shared/function'
import { logicIssues } from '~/logic/common/issue'
import { ISSUE_TYPE_CODE_ENUM } from '@/shared/constants'
import { FILE_UPLOAD_TYPE, LOAD_STATE } from '@/shared/types'
import { aiTitleApi } from '~/server'

const emits = defineEmits({
  changeCurrentStep: (_step: number) => true,
  submit: () => true,
})
const { state: issuesState, issues, getHandle: getIssuesHandle } = logicIssues({ clientOnly: true })
const { formData } = storeToRefs(usePublishStore())
const formRef = ref<FormInst | null>(null)
const rules: FormRules = {
  // 至少选择一个问题
  quality: [
    {
      validator: (_rule: FormItemRule, _value) => {
        const { quality, sale, afterSale } = formData.value
        if (!quality.length && !sale.length && !afterSale.length)
          return new Error('至少选择一个问题')
        return true
      },
    },
  ],
  appeal: [
    {
      required: true,
      message: '请选择您的诉求',
    },
  ],
  title: [
    {
      required: true,
      message: '请输入投诉标题',
      trigger: 'blur',
    },
    {
      max: 50,
      message: '投诉标题不能超过50个字符',
      trigger: 'blur',
    },
  ],
  content: [
    {
      required: true,
      message: '请输入投诉内容',
      trigger: 'blur',
    },
    {
      min: 50,
      message: '投诉内容不能少于50个字符',
      trigger: 'change',
    },
  ],
}

// 再次投诉时是否展示上次投诉的内容
const isHide = ref(false)
async function validator() {
  const { isOk } = await validateHandle(formRef)
  if (!isOk)
    return false

  emits('submit')
}

getIssuesHandle()

defineExpose({
  formRef,
})
const editorRef = ref()
const showModal = ref(false)
const titleData = reactive({
  list: [] as string[],
  loading: false,
})
async function generatedAi() {
  const content = editorRef.value.getContentText()
  console.log('🚀 ~ generatedAi ~ content:', content)
  if (content.length < 30)
    return useMessage.error('请先输入至少30字的投诉内容')

  showModal.value = true
  titleData.loading = true
  const { data } = await aiTitleApi({ content })
  titleData.list = data
  titleData.loading = false
}
</script>

<template>
  <div>
    <n-modal v-model:show="showModal" title="选择标题" class="w-110 min-h-80" preset="card">
      <div v-if="titleData.loading">
        <n-skeleton class="h-10" text :repeat="5" />
      </div>
      <div v-else>
        <div v-for="i in titleData.list" :key="i" class="border-b-1 cursor-pointer border-slate-2 py-2 " @click="() => { formData.title = i, showModal = false }">
          {{ i }}
        </div>
      </div>
    </n-modal>
    <div class="bg-white rounded-md p-4xl mt-4xl">
      <UtilLoadState :state="issuesState" :reload-handler="getIssuesHandle">
        <NForm
          ref="formRef"
          :model="formData"
          :rules="rules"
          :label-width="150"
          label-placement="left"
          size="medium"
        >
          <NGrid :cols="24" :x-gap="24">
            <NFormItemGi first :span="20" path="quality" label="质量问题部位">
              <Quality
                v-model="formData.quality"
                :state="issuesState"
                :car-series-energy-code="formData.carSeriesEnergyCode"
                :reload-handler="getIssuesHandle"
                :issues="issues[ISSUE_TYPE_CODE_ENUM.quality]"
              />
            </NFormItemGi>
            <NFormItemGi first :span="20" label="销售问题">
              <Quality
                v-model="formData.sale"
                :state="issuesState"
                :reload-handler="getIssuesHandle"
                :issues="issues[ISSUE_TYPE_CODE_ENUM.sale]"
              />
              <!-- <BasicCheckBoxGroup v-model="formData.sale" :options="issues[ISSUE_TYPE_CODE_ENUM.sale]" /> -->
            </NFormItemGi>
            <NFormItemGi first :span="20" label="售后问题">
              <Quality
                v-model="formData.afterSale"
                :state="issuesState"
                :reload-handler="getIssuesHandle"
                :issues="issues[ISSUE_TYPE_CODE_ENUM.afterSale]"
              />
              <!-- <BasicCheckBoxGroup v-model="formData.afterSale" :options="issues[ISSUE_TYPE_CODE_ENUM.afterSale]" /> -->
            </NFormItemGi>
            <NFormItemGi first :span="24" label="您的诉求" required path="appeal">
              <BasicCheckBoxGroup v-model="formData.appeal" :options="issues[ISSUE_TYPE_CODE_ENUM.appeal]" />
            </NFormItemGi>

            <NFormItemGi first :span="24" label="投诉内容" required path="content">
              <div class="w-full">
                <Editor ref="editorRef" v-model="formData.content">
                  <div v-if="formData.refComplainId">
                    <div
                      class="flex items-center justify-between px-3 cursor-pointer border-b-1 border-slate-2 py-2"
                      :class="[{ 'is-hide': !isHide }]" @click="isHide = !isHide"
                    >
                      <span class="font-bold">上次投诉相关内容</span>
                      <span class="text-blue-5">{{ isHide ? "展开" : "收起" }}</span>
                    </div>
                    <div
                      v-if="isHide && formData.refComplainContent && formData.refComplainId"
                      class="p-sm border-b-1 border-slate-2 refContent "
                    >
                      <div
                        class="bg-slate-1 p-sm rounded-md"
                        v-html="formData.refComplainContent"
                      />
                    </div>
                  </div>
                </Editor>
              </div>
            </NFormItemGi>

            <NFormItemGi first :span="24" label="投诉标题" required path="title">
              <div class="flex w-full">
                <div class="btn-primary cursor-pointer   text-4.5 font-bold color-white line-height-snug  flex-center rounded mr-3 p-4 h-20 w-20 flex-col" @click="generatedAi">
                  <span> 智能</span>
                  <span> 生成</span>
                </div>
                <NInput v-model:value="formData.title" type="textarea" maxlength="50" show-count clearable placeholder="请输入投诉标题...（提交时自动拼接品牌和车系）" />
              </div>
            </NFormItemGi>

            <NFormItemGi :span="24" label="图片上传">
              <BasicUpload
                v-model="formData.pictures"
                :file-size="5"
                :file-type="FILE_UPLOAD_TYPE.complain"
              />
            </NFormItemGi>
            <NFormItemGi :span="24" label="视频上传">
              <BasicUpload
                v-model="formData.videos"
                list-type="text"
                accept="video/*"
                :file-size="50"
                :file-type="FILE_UPLOAD_TYPE.complain"
              >
                <div class="flex items-center">
                  <button class="btn-primary">
                    上传视频
                  </button>
                  <div class="text-slate-4 text-3 ml-3">
                    (上传视频更容易获得良好处理,单文件限制50M)
                  </div>
                </div>
              </BasicUpload>
            </NFormItemGi>
            <NFormItemGi :span="24" label="音频上传">
              <BasicUpload
                v-model="formData.audios"
                list-type="text"
                :file-size="5"
                accept="audio/*"
                :file-type="FILE_UPLOAD_TYPE.complain"
              >
                <div class="flex items-center">
                  <button class="btn-primary">
                    上传音频
                  </button>
                  <div class="text-slate-4 text-3 ml-3">
                    (单文件限制5M)
                  </div>
                </div>
              </BasicUpload>
            </NFormItemGi>
          </NGrid>
        </NForm>
      </UtilLoadState>
    </div>
    <div class="flex items-center justify-center mt-xl">
      <button class="btn-default px-4xl mr-xl" @click="emits('changeCurrentStep', 1)">
        上一步
      </button>
      <button class="btn-primary px-12" @click="validator">
        发布
      </button>
    </div>
  </div>
</template>

<style lang='scss' scoped>
:deep(*){
  .n-tabs-nav-scroll-wrapper{
    --uno: px-sm bg-slate-1;
  }
}
.refContent {
  word-break: break-all;
}
</style>
